import DimensionsService from './dimensionsService';
import plotsService from './plotsService';
import { getRumNameFromResource } from '@splunk/olly-imm/build/detectors/utils/rumFunctions';
export const APM_IDENTIFIERS = ['ep_above_ft', 'd_above_ft', 'signal_baseline'];
export const APM_ALERT_TYPES = ['errors', 'latency', 'requests'];
import { capitalize } from 'lodash';

export default class ChartInformationService {
    constructor(detectorPriorityService, valueFormatter, signalTypeService, featureEnabled) {
        this.detectorPriorityService = detectorPriorityService;
        this.valueFormatter = valueFormatter;
        this.signalTypeService = signalTypeService;
        this.featureEnabled = featureEnabled;
        this.dimensionsService = new DimensionsService(featureEnabled);
    }

    getChartInformation(event, incidentInfo, orgId, plotColorMap) {
        const incidentRelatedPlots = plotsService.getPlotsForIncident(event, incidentInfo);
        const triggerValue = plotsService.getTriggerValue(
            incidentRelatedPlots,
            this.valueFormatter.formatValue
        );
        return {
            swatchClass: this._getSwatchClass(event.metadata.sf_severity),
            title: this.buildChartTitle(event, incidentInfo, incidentRelatedPlots),
            plots: plotsService.getPlotsInformation(
                incidentRelatedPlots,
                event,
                this.valueFormatter.formatValue,
                plotColorMap
            ),
            condition: event.metadata.sf_detectOnCondition,
            triggerValue: triggerValue,
            dimensions: this.dimensionsService.getForPlots(incidentRelatedPlots, orgId),
        };
    }

    buildChartTitle(event, incidentInfo, relatedPlots) {
        if (event?.properties?.detector_type === 'rum') {
            return this.buildForRUMDetector(event, relatedPlots);
        }

        if (event?.metadata?.sf_eventSloId) {
            return this.buildForSlo(event, relatedPlots);
        }

        const isAPMTypeDetector =
            event?.properties?.detector_type === 'apm' &&
            APM_ALERT_TYPES.includes(event?.properties?.alert_type);
        // hasAPMIdentifiers can be removed after APM detectors migration
        const hasAPMIdentifiers =
            incidentInfo && APM_IDENTIFIERS.some((id) => id === incidentInfo.conditionIdentifier);
        if (isAPMTypeDetector || hasAPMIdentifiers) {
            return this.buildForAPMDetector(event, relatedPlots);
        }
        return this.buildForInfrastructureDetector(relatedPlots, incidentInfo);
    }

    getValueString(value, threshold, sufix = '') {
        if (value && threshold) {
            return `(value: ${value}${sufix}) > ${threshold}${sufix}`;
        }
        return '';
    }

    buildForSlo(event, relatedPlots) {
        function getSloAlertTypeFromAlertName(alertType) {
            switch (alertType) {
                case 'BREACH':
                    return 'Breach';
                case 'ERROR_BUDGET_LEFT':
                    return 'Error Budget Left';
                case 'BURN_RATE':
                    return 'Burn Rate';
                default:
                    return `${capitalize(alertType)}`;
            }
        }
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getSloThresholdValue(relatedPlots);
        return `SLO ${getSloAlertTypeFromAlertName(event.metadata.sf_eventSloAlertType)} Event ${
            value && threshold ? `(value: ${value}) < ${threshold}` : ''
        }`;
    }

    buildForAPMDetector(event, relatedPlots) {
        const LATENCY_SUFFIX = 'ms';
        const ERROR_RATE_SUFFIX = '%';
        const service = event.metadata[this.signalTypeService.getAPMDimensions().service];
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const environment = event.metadata[this.signalTypeService.getAPMDimensions().environment];
        const workflow = event.metadata[this.signalTypeService.getAPMDimensions().workflow];
        const useTransactionTerminology = this.featureEnabled(
            'apm2RenameWorkflowToTransactionInUI'
        );
        const workflowLabel = useTransactionTerminology ? 'Business Transaction' : 'Workflow';

        const buildTitle = (type, alertType, service, environment, value, threshold) => {
            switch (alertType) {
                case 'latency':
                    return `APM ${type} Latency - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold,
                        LATENCY_SUFFIX
                    )}`;
                case 'requests':
                    return `APM ${type} Requests - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold
                    )}`;
                case 'errors':
                    return `APM ${type} Error rate - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold,
                        ERROR_RATE_SUFFIX
                    )}`;
                default:
                    return `APM ${type} - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold
                    )}`;
            }
        };

        if (workflow) {
            return buildTitle(
                workflowLabel,
                event.properties.alert_type,
                workflow,
                environment,
                value,
                threshold
            );
        }
        return buildTitle(
            'Service',
            event.properties.alert_type,
            service,
            environment,
            value,
            threshold
        );
    }

    buildForRUMDetector(event, relatedPlots) {
        const alert_type = event.properties.alert_type;
        const sufix = alert_type === 'latency' ? 'ms' : alert_type === 'error_rate' ? '%' : '';
        const value = plotsService.getTriggerValue(relatedPlots, this.valueFormatter.formatValue);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const rumFunctionLabel = getRumNameFromResource(event.properties.resource_type, alert_type);
        return `RUM ${rumFunctionLabel} ${this.getValueString(value, threshold, sufix)}`;
    }

    buildForInfrastructureDetector(relatedPlots, incidentInfo) {
        if (incidentInfo && incidentInfo.builtInFunction && incidentInfo.readableCondition) {
            return incidentInfo.readableCondition;
        }

        const metricName = plotsService.getTriggerPlotName(relatedPlots);
        let value = plotsService.getTriggerValue(relatedPlots, this.valueFormatter.formatValue);
        if (value === '-') {
            value = 'N/A';
        }

        return metricName && value ? `${metricName} (value: ${value})` : '';
    }

    _getSwatchClass(severityName) {
        const severity = this.detectorPriorityService.getSeverityByDisplay(severityName);

        return this.detectorPriorityService.getSwatchClassBySeverity(severity);
    }
}
